import { useMemo, Fragment } from 'react';
import { Box, Divider, InputLabel, Typography } from '@mui/material';

import { defaultLanguage } from 'context';
import { useCourseContext } from 'context';
import { ScaleRatingFormField } from 'types';

import { ScaleRatingField } from '../fields';

const sharedHeaderLabelSx = {
  whiteSpace: 'normal',
  color: 'gray.neutral',

  paddingBottom: '16px',
} as const;

interface ScaleHeaderProps {
  min: number;
  count: number;
  labels: string[];
  hasAllLabels: boolean;
  showSimpleHeader: boolean;
}

const ScaleHeader = ({
  min,
  count,
  labels,
  hasAllLabels,
  showSimpleHeader,
}: ScaleHeaderProps) => {
  if (showSimpleHeader) {
    return (
      <div className="mb-1 flex w-full items-center justify-between">
        <Typography variant="body1" sx={sharedHeaderLabelSx}>
          {labels[0]}
        </Typography>
        <Typography variant="body1" sx={sharedHeaderLabelSx}>
          {labels[count - 1]}
        </Typography>
      </div>
    );
  }

  if (hasAllLabels) {
    return (
      <div className="mb-1 flex w-full items-start gap-2">
        {labels.map((labelText, labelIndex) => (
          <Typography
            variant="body1"
            key={min + labelIndex}
            sx={sharedHeaderLabelSx}
            className="flex flex-1 justify-center text-center leading-tight break-words"
          >
            {labelText}
          </Typography>
        ))}
      </div>
    );
  }

  return (
    <div className="mb-1 flex items-end gap-2 md:gap-2.5">
      {labels.map((labelText, labelIndex) => (
        <Typography
          variant="body1"
          key={min + labelIndex}
          className="w-7 text-center leading-tight break-words md:w-8"
          sx={{
            ...sharedHeaderLabelSx,
            display: 'flex',
            alignItems: 'flex-end',
            justifyContent: 'center',
          }}
        >
          {labelText}
        </Typography>
      ))}
    </div>
  );
};

export const FormScaleRatingField = ({
  id,
  label,
  options,
  translations,
}: ScaleRatingFormField) => {
  const { language } = useCourseContext();
  const min = options?.minValue ?? 1;
  const max = options?.maxValue ?? 7;
  const count = max - min + 1;

  const translation = useMemo(() => {
    if (!language || !translations || language === defaultLanguage) {
      return {
        label,
        options: options?.options,
        valueLabels: options?.valueLabels,
      };
    }

    const fieldTranslation = translations[language];
    return {
      options: fieldTranslation.options || options?.options,
      label: label ? fieldTranslation?.label || label : label,
      valueLabels: fieldTranslation.valueLabels || options?.valueLabels,
    };
  }, [label, options?.options, options?.valueLabels, language, translations]);

  const { labels, hasAllLabels, showFullHeader, showSimpleHeader } =
    useMemo(() => {
      const computedLabels = Array.from(
        { length: count },
        (_, i) => translation.valueLabels?.[i]?.trim() ?? ''
      );
      const hasAnyLabel = computedLabels.some(Boolean);
      const hasAllLabels = computedLabels.every(Boolean);
      const hasFirst = !!computedLabels[0];
      const hasLast = !!computedLabels[count - 1];
      const intermediateCount = computedLabels
        .slice(1, Math.max(1, count - 1))
        .filter(Boolean).length;
      const showSimpleHeader = (hasFirst || hasLast) && intermediateCount === 0;

      return {
        hasAllLabels,
        showSimpleHeader,
        labels: computedLabels,
        showFullHeader: hasAnyLabel && !showSimpleHeader,
      };
    }, [count, translation.valueLabels]);

  return (
    <Box className="flex flex-col">
      {translation.label && (
        <InputLabel className="mb-2" sx={{ whiteSpace: 'pre-line' }}>
          {translation.label}
        </InputLabel>
      )}
      {translation.options?.map((option, index) => (
        <Fragment key={`${id}-${option}-${index}`}>
          <ScaleRatingField
            min={min}
            max={max}
            label={option}
            name={`${id}.${index}`}
            stretchLayout={hasAllLabels}
            labelClassName={index === 0 ? 'md:pt-13' : undefined}
            header={
              index === 0 && (showFullHeader || showSimpleHeader) ? (
                <ScaleHeader
                  min={min}
                  count={count}
                  labels={labels}
                  hasAllLabels={hasAllLabels}
                  showSimpleHeader={showSimpleHeader}
                />
              ) : undefined
            }
          />
          {index < translation.options!.length - 1 && (
            <Divider sx={{ my: 1.5, borderColor: 'gray.divider' }} />
          )}
        </Fragment>
      ))}
    </Box>
  );
};
